import React, { useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import {
  BarChart3,
  Calendar,
  DollarSign,
  FileSpreadsheet,
  FileText,
  Leaf,
  ShoppingCart,
  Truck
} from 'lucide-react';
import { isSameMonth } from 'date-fns';
import { useAppContext } from '../context/AppContext';
import { useConstruction } from '../context/ConstructionContext';
import { useModuleSettings } from '../context/ModuleSettingsContext';
import { buildConstructionReports } from '../construction/reports';
import { HardHat } from 'lucide-react';
import { formatMoney, formatDate } from '../utils/helpers';
import Button from './common/Button';
import StatCard from './StatCard';
import { t } from '../i18n';

const sanitizeCsvCell = (value) => {
  if (value == null) return '';
  const str = String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replaceAll('"', '""')}"`;
  }
  return str;
};

const downloadCsv = (filename, headers, rows) => {
  const csvContent = [headers, ...rows]
    .map((row) => row.map(sanitizeCsvCell).join(','))
    .join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const downloadPdf = (filename, title, rows) => {
  const doc = new jsPDF();
  doc.setFontSize(18);
  doc.text(title, 14, 20);
  doc.setFontSize(11);
  let positionY = 32;
  rows.forEach((row) => {
    doc.text(row, 14, positionY);
    positionY += 8;
    if (positionY > 280) {
      doc.addPage();
      positionY = 20;
    }
  });
  doc.save(filename);
};

const Reports = () => {
  const {
    purchaseOrders,
    purchaseRequisitions,
    inventory,
    suppliers,
    shipments,
    movements,
    locations,
    parks,
    projectFinancialOverview,
    projectTimelines,
    renewableProjects,
    renewablePermits,
    preferences,
    findCostCenterById,
    findAreaById,
    findParkById
  } = useAppContext();

  const { summaries: constructionSummaries, state: constructionState, convert: convertConstruction, userName } = useConstruction();
  const { isModuleActive } = useModuleSettings();
  const nowDate = new Date();
  const [selectedArea, setSelectedArea] = useState('development');
  const [selectedProjectId, setSelectedProjectId] = useState('all');

  const displayCurrency = preferences?.displayCurrency === 'CLP' ? 'CLP' : 'USD';
  const fxUsdClp = Number(preferences?.fxUsdClp || 950);
  const convertAmount = (amount, fromCurrency, toCurrency) => {
    const value = Number(amount || 0);
    const from = fromCurrency === 'CLP' ? 'CLP' : 'USD';
    const to = toCurrency === 'CLP' ? 'CLP' : 'USD';
    if (from === to) return value;
    if (!Number.isFinite(fxUsdClp) || fxUsdClp <= 0) return value;
    if (from === 'CLP' && to === 'USD') return value / fxUsdClp;
    if (from === 'USD' && to === 'CLP') return value * fxUsdClp;
    return value;
  };

  const resolveParkId = (entity) => {
    if (!entity) return null;
    if (entity.parkId) return entity.parkId;
    if (entity.areaId) return findAreaById(entity.areaId)?.parkId || null;
    return null;
  };

  const selectedParkId = selectedProjectId === 'all' ? null : selectedProjectId;
  const filterByProject = (entity) => {
    if (!selectedParkId) return true;
    return resolveParkId(entity) === selectedParkId;
  };

  const monthlySpend = useMemo(() => {
    return purchaseOrders
      .filter((order) => isSameMonth(new Date(`${order.createdDate}T00:00:00`), nowDate))
      .reduce(
        (sum, order) => sum + convertAmount(order.totalValue || 0, order.currency || 'CLP', displayCurrency),
        0
      );
  }, [purchaseOrders, nowDate, displayCurrency, fxUsdClp]);

  const inventoryValueClp = useMemo(() => {
    return inventory.reduce(
      (sum, item) =>
        sum + item.locations.reduce((acc, location) => acc + location.quantity * item.unitValue, 0),
      0
    );
  }, [inventory]);

  const inventoryValue = convertAmount(inventoryValueClp, 'CLP', displayCurrency);
  const pendingShipments = shipments.filter((shipment) => shipment.status !== 'recepcionado').length;

  const areaButtons = [
    { id: 'development', label: t('Desarrollo'), icon: Leaf },
    { id: 'project_management', label: t('Project management'), icon: Calendar },
    { id: 'procurement', label: t('Procurement'), icon: ShoppingCart },
    { id: 'operations', label: t('Operaciones'), icon: Truck },
    ...(isModuleActive('construction-module') ? [{ id: 'construction', label: t('Construcción'), icon: HardHat }] : []),
    { id: 'cost_control', label: t('Control de costos'), icon: DollarSign }
  ];

  const reports = useMemo(() => {
    const projectLabel = selectedParkId ? findParkById(selectedParkId)?.name || selectedParkId : 'todos';
    const suffix = selectedParkId ? `_${selectedParkId}` : '';

    if (selectedArea === 'construction') {
      return buildConstructionReports({
        summaries: constructionSummaries,
        state: constructionState,
        selectedParkId,
        projectLabel,
        suffix,
        displayCurrency,
        convert: convertConstruction,
        userName,
        downloadCsv,
        downloadPdf
      });
    }

    if (selectedArea === 'development') {
      return [
        {
          id: 'permits',
          title: t('Permisos/PAS (CSV)'),
          description: t('Listado de permisos, estado, avance, responsable y fecha objetivo.'),
          icon: FileSpreadsheet,
          action: () => {
            const headers = ['Proyecto', 'Permiso', 'Tipo', 'Estado', 'Avance %', 'Fecha objetivo', 'Responsable'];
            const renewableByPark = new Map(renewableProjects.map((p) => [p.parkId, p]));
            const rows = renewablePermits
              .filter((permit) => {
                if (!selectedParkId) return true;
                const project = renewableByPark.get(selectedParkId);
                return project ? permit.projectId === project.id : false;
              })
              .map((permit) => {
                const project = renewableProjects.find((p) => p.id === permit.projectId) || null;
                const park = project?.parkId ? findParkById(project.parkId) : null;
                return [
                  park?.name || project?.name || '-',
                  permit.name,
                  permit.type || '-',
                  permit.status || '-',
                  permit.progress ?? 0,
                  permit.expectedDate || '-',
                  permit.responsible || '-'
                ];
              });
            downloadCsv(`alcor_permisos_pas${suffix}.csv`, headers, rows);
          }
        }
      ];
    }

    if (selectedArea === 'project_management') {
      return [
        {
          id: 'timeline',
          title: t('Cronograma (Gantt) (CSV)'),
          description: t('Tareas, fechas y avance para {0}.', projectLabel),
          icon: FileSpreadsheet,
          action: () => {
            const headers = ['Proyecto', 'Partida', 'Subpartida', 'Tarea', 'Inicio', 'Fin', 'Avance %', 'Owner'];
            const rows = projectTimelines
              .filter((timeline) => (!selectedParkId ? true : timeline.parkId === selectedParkId))
              .flatMap((timeline) =>
                (timeline.tasks || []).map((task) => [
                  findParkById(timeline.parkId)?.name || timeline.parkId,
                  task.partida || '-',
                  task.subPartida || '-',
                  task.name || '-',
                  task.start || '-',
                  task.end || '-',
                  task.progress ?? 0,
                  task.owner || '-'
                ])
              );
            downloadCsv(`alcor_cronograma${suffix}.csv`, headers, rows);
          }
        }
      ];
    }

    if (selectedArea === 'procurement') {
      return [
        {
          id: 'requisitions',
          title: t('Requisiciones (PR) (CSV)'),
          description: t('PR por estado, solicitante, parque/área y total estimado.'),
          icon: FileSpreadsheet,
          action: () => {
            const headers = ['PR', 'Título', 'Estado', 'Fecha', 'Parque', 'Área', 'Solicitante', 'Total estimado'];
            const rows = purchaseRequisitions
              .filter(filterByProject)
              .map((req) => {
                const parkId = resolveParkId(req);
                const area = req.areaId ? findAreaById(req.areaId) : null;
                return [
                  req.id,
                  req.title || '-',
                  req.status || '-',
                  req.createdDate || '-',
                  parkId ? findParkById(parkId)?.name || parkId : '-',
                  area?.name || '-',
                  req.requesterName || req.requesterId || '-',
                  Number(req.estimatedTotal || 0)
                ];
              });
            downloadCsv(`alcor_pr${suffix}.csv`, headers, rows);
          }
        },
        {
          id: 'orders',
          title: t('Órdenes de compra (OC) (CSV)'),
          description: t('OC con moneda, monto y estado ({0}).', displayCurrency),
          icon: FileSpreadsheet,
          action: () => {
            const headers = [
              'OC',
              'Proveedor',
              'Estado',
              'Fecha',
              'Parque',
              'Centro costo',
              'Moneda',
              `Monto ${displayCurrency}`
            ];
            const rows = purchaseOrders
              .filter(filterByProject)
              .map((order) => {
                const parkId = resolveParkId(order);
                const amount = convertAmount(order.totalValue || 0, order.currency || 'CLP', displayCurrency);
                const costCenter = order.costCenterId ? findCostCenterById(order.costCenterId) : null;
                return [
                  order.id,
                  order.supplierName || '-',
                  order.status || '-',
                  order.createdDate || '-',
                  parkId ? findParkById(parkId)?.name || parkId : '-',
                  costCenter?.code || '-',
                  order.currency || 'CLP',
                  Math.round(amount)
                ];
              });
            downloadCsv(`alcor_oc${suffix}.csv`, headers, rows);
          }
        },
        {
          id: 'purchases_by_supplier',
          title: t('Compras por proveedor (CSV)'),
          description: t('Ranking por monto ({0}) para {1}.', displayCurrency, projectLabel),
          icon: ShoppingCart,
          action: () => {
            const relevantOrders = purchaseOrders.filter(filterByProject);
            const headers = ['Proveedor', 'Riesgo', 'Órdenes', `Monto ${displayCurrency}`];
            const rows = suppliers.map((supplier) => {
              const supplierOrders = relevantOrders.filter((order) => order.supplierId === supplier.id);
              const totalValue = supplierOrders.reduce(
                (sum, order) => sum + convertAmount(order.totalValue || 0, order.currency || 'CLP', displayCurrency),
                0
              );
              return [supplier.name, supplier.riskLevel || '-', supplierOrders.length, Math.round(totalValue)];
            });
            downloadCsv(`alcor_compras_por_proveedor${suffix}.csv`, headers, rows);
          }
        }
      ];
    }

    if (selectedArea === 'operations') {
      return [
        {
          id: 'inventory',
          title: t('Inventario total (CSV)'),
          description: t('Detalle de materiales por SKU, stock total y valor consolidado.'),
          icon: FileSpreadsheet,
          action: () => {
            const headers = [
              'SKU',
              'Descripción',
              'Categoría',
              'Parque Principal',
              'Stock Total',
              `Valor Total ${displayCurrency}`
            ];
            const rows = inventory
              .filter((item) => (!selectedParkId ? true : item.primaryParkId === selectedParkId))
              .map((item) => {
                const quantity = item.locations.reduce((sum, location) => sum + location.quantity, 0);
                const valueClp = item.locations.reduce((sum, location) => sum + location.quantity * item.unitValue, 0);
                const value = convertAmount(valueClp, 'CLP', displayCurrency);
                const park = item.primaryParkId ? findParkById(item.primaryParkId) : null;
                return [item.sku, item.description, item.category, park?.name || '-', quantity, Math.round(value)];
              });
            downloadCsv(`alcor_inventario${suffix}.csv`, headers, rows);
          }
        },
        {
          id: 'shipments',
          title: t('Despachos (CSV)'),
          description: t('Envíos a terreno con estado, fechas y proyecto.'),
          icon: Truck,
          action: () => {
            const headers = ['ID', 'Estado', 'Fecha', 'Proyecto', 'Destino', 'Items'];
            const rows = shipments
              .filter(filterByProject)
              .map((shipment) => {
                const parkId = resolveParkId(shipment);
                return [
                  shipment.id,
                  shipment.status || '-',
                  shipment.createdDate || shipment.date || '-',
                  parkId ? findParkById(parkId)?.name || parkId : '-',
                  shipment.destination || '-',
                  Array.isArray(shipment.items) ? shipment.items.length : 0
                ];
              });
            downloadCsv(`alcor_despachos${suffix}.csv`, headers, rows);
          }
        },
        {
          id: 'traceability',
          title: t('Movimientos de trazabilidad (CSV)'),
          description: t('Ingresos, salidas y transferencias con ubicación y referencia.'),
          icon: FileSpreadsheet,
          action: () => {
            const headers = ['Fecha', 'Tipo', 'SKU', 'Cantidad', 'Ubicación', 'Proyecto', 'Centro Costo', 'Referencia'];
            const rows = movements
              .filter(filterByProject)
              .map((movement) => [
                formatDate(movement.date),
                movement.type,
                movement.sku,
                movement.quantity,
                locations.find((loc) => loc.id === movement.locationId)?.name || '-',
                movement.parkId ? findParkById(movement.parkId)?.name || '-' : '-',
                movement.costCenterId ? findCostCenterById(movement.costCenterId)?.code || '-' : '-',
                movement.reference
              ]);
            downloadCsv(`alcor_movimientos_trazabilidad${suffix}.csv`, headers, rows);
          }
        }
      ];
    }

    return [
      {
        id: 'budget',
        title: t('Presupuesto vs gasto (CSV)'),
        description: t(
          'Comparación de presupuesto y gasto ({0}) para {1}.',
          displayCurrency,
          projectLabel
        ),
        icon: BarChart3,
        action: () => {
          const headers = [
            'Proyecto',
            `Presupuesto ${displayCurrency}`,
            `Comprometido ${displayCurrency}`,
            `Facturado ${displayCurrency}`,
            'Moneda base'
          ];
          const rows = projectFinancialOverview
            .filter((summary) => (!selectedParkId ? true : summary.parkId === selectedParkId))
            .map((summary) => {
              const budget = convertAmount(summary.totalBudget || 0, summary.currency || 'USD', displayCurrency);
              const committed = convertAmount(summary.totalCommitted || 0, summary.currency || 'USD', displayCurrency);
              const invoiced = convertAmount(summary.totalInvoiced || 0, summary.currency || 'USD', displayCurrency);
              return [
                summary.parkName,
                Math.round(budget),
                Math.round(committed),
                Math.round(invoiced),
                summary.currency || 'USD'
              ];
            });
          downloadCsv(`alcor_presupuesto_vs_gasto${suffix}.csv`, headers, rows);
        }
      },
      {
        id: 'services',
        title: t('Servicios contratados (PDF)'),
        description: t(
          'Servicios nacionales contratados con fecha, proveedor y monto ({0}).',
          displayCurrency
        ),
        icon: FileText,
        action: () => {
          const serviceOrders = purchaseOrders.filter((order) => order.type === 'nacional').filter(filterByProject);
          const rows = serviceOrders.map((order) => {
            const value = convertAmount(order.totalValue || 0, order.currency || 'CLP', displayCurrency);
            return `${order.id} · ${order.supplierName} · ${formatDate(order.createdDate)} · ${formatMoney(
              value,
              displayCurrency
            )}`;
          });
          downloadPdf(`alcor_servicios_contratados${suffix}.pdf`, 'Servicios contratados', rows);
        }
      }
    ];
  }, [
    selectedArea,
    selectedParkId,
    displayCurrency,
    fxUsdClp,
    renewableProjects,
    renewablePermits,
    projectTimelines,
    purchaseRequisitions,
    purchaseOrders,
    suppliers,
    inventory,
    shipments,
    movements,
    locations,
    projectFinancialOverview,
    filterByProject,
    findParkById,
    findCostCenterById,
    findAreaById,
    constructionSummaries,
    constructionState,
    convertConstruction,
    userName
  ]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-neutral-900">{t('Reportes y Descargas')}</h1>
        <p className="text-sm text-neutral-500">{t('Selecciona un área y un proyecto para descargar reportes CSV/PDF.')}</p>
      </div>

      <div className="bg-white border border-neutral-200 rounded p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-neutral-900">{t('Centro de reportes')}</h2>
            <p className="text-xs text-neutral-500">{t('Filtra por área y por proyecto.')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-neutral-500">{t('Proyecto')}</span>
              <select
                value={selectedProjectId}
                onChange={(event) => setSelectedProjectId(event.target.value)}
                className="h-8 px-2.5 border border-neutral-300 rounded bg-white text-sm focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
              >
                <option value="all">{t('Todos')}</option>
                {parks.map((park) => (
                  <option key={park.id} value={park.id}>
                    {t(park.name)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-neutral-500">{t('Moneda')}</span>
              <span className="h-8 inline-flex items-center px-2.5 border border-neutral-200 rounded bg-neutral-50 text-sm font-medium text-neutral-700">
                {t(displayCurrency)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {areaButtons.map((area) => {
            const Icon = area.icon;
            const enabled = selectedArea === area.id;
            return (
              <Button
                key={area.id}
                variant={enabled ? 'primary' : 'secondary'}
                size="sm"
                icon={Icon}
                onClick={() => setSelectedArea(area.id)}
              >
                {t(area.label)}
              </Button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard title={t('Compras mes en curso')} value={formatMoney(monthlySpend, displayCurrency)} icon={BarChart3} />
        <StatCard title={t('Valor inventario')} value={formatMoney(inventoryValue, displayCurrency)} icon={FileSpreadsheet} />
        <StatCard title={t('Despachos pendientes')} value={pendingShipments.toString()} icon={Truck} />
        <StatCard title={t('Órdenes totales')} value={purchaseOrders.length.toString()} icon={ShoppingCart} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reports.map((report) => {
          const Icon = report.icon;
          return (
            <div
              key={report.id}
              className="bg-white border border-neutral-200 rounded p-4 flex flex-col justify-between"
            >
              <div className="flex items-start gap-3">
                <Icon className="w-5 h-5 text-neutral-400 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900">{t(report.title)}</h3>
                  <p className="text-xs text-neutral-500 mt-1">{t(report.description)}</p>
                </div>
              </div>
              <div className="mt-4">
                <Button variant="primary" size="sm" onClick={report.action}>{t('Descargar')}</Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Reports;
